import { Spacing } from '@persona-id/design-tokens';
import { memo, ReactNode, useMemo } from 'react';
import styled from 'styled-components';

import useHasAccessForTierDeterminator from 'dashboard/apps/InquiryTemplateEditor/hooks/useHasAccessForTierDeterminator';
import { EditorAccessTier } from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import InquiriesGatingSticker, {
  EnhancedGatingStickerType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiriesGatingSticker';
import { ComponentSchemaValues } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/constants';
import ComponentLibraryBlock from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateScreenComponentEditor/ScreenEditorComponentLibrary/ComponentLibraryBlock';
import ProductUpsellTooltip, {
  getProductUpsellLevel,
} from 'dashboard/components/ProductUpsellTooltip';
import { Feature } from 'dashboard/models/inquiry-template-version';
import { Z_INDICES } from 'dashboard/styles/variables';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';
import { FeatureFlag } from 'lib/interfaces/feature-flags';

const InquiriesGatingStickerContainer = styled.div`
  position: absolute;
  top: ${Spacing[2]};
  left: ${Spacing[2]};
  user-select: none;
  pointer-events: none;
`;

type SelfServeComponentBlockWrapperProps = {
  icon: ReactNode;
  label: string;
  config: ComponentSchemaValues['config'];
  featureFlagInfo: {
    flag: FeatureFlag;
    renderValue: boolean;
  };
  accessTier: EditorAccessTier;
  disableAllComponents?: boolean;
  disableImageComponentBlock?: boolean;
};

const SelfServeComponentBlockWrapper = ({
  icon,
  label,
  featureFlagInfo,
  config,
  accessTier,
  disableAllComponents,
  disableImageComponentBlock,
}: SelfServeComponentBlockWrapperProps) => {
  const { flag, renderValue } = featureFlagInfo || {};
  const featureFlagEnabled = useFeatureFlag(flag);
  const isAccessibleAtTier = useHasAccessForTierDeterminator()(accessTier);

  const enableInteraction =
    !disableAllComponents && !disableImageComponentBlock && isAccessibleAtTier;

  const gatedSettings = useMemo(() => {
    if (!disableAllComponents) {
      if (disableImageComponentBlock) {
        return {
          badge: (
            <InquiriesGatingSticker stickerType={EnhancedGatingStickerType.Limited} iconOnly />
          ),
          tooltipText: `Upgrade to the ${getProductUpsellLevel(
            EditorAccessTier.InquiriesBasic,
          )} plan to use this component on all screens. `,
        };
      } else if (!isAccessibleAtTier) {
        return {
          badge: (
            <InquiriesGatingSticker stickerType={EnhancedGatingStickerType.Limited} iconOnly />
          ),
          productUpsellLevel: getProductUpsellLevel(accessTier),
          tooltipText: `This component is available on the ${getProductUpsellLevel(
            accessTier,
          )} plan. `,
        };
      }
    }

    return null;
  }, [disableImageComponentBlock, isAccessibleAtTier, disableAllComponents, accessTier]);

  if (flag != null && featureFlagEnabled !== renderValue) {
    return null;
  }

  const libraryBlockProps = {
    config,
    icon,
    label,
    disabled: !enableInteraction,
  };

  return gatedSettings != null ? (
    <ProductUpsellTooltip
      id={`component-library-block-${label}`}
      portal
      zIndex={Z_INDICES.topNavTooltip}
      customContent={gatedSettings?.tooltipText}
      productUpsellLevel={gatedSettings?.productUpsellLevel}
      accessTier={accessTier}
      learnMoreId={`component-${label}`}
      feature={accessTier === EditorAccessTier.InquiriesEnhanced ? Feature.EnhancedComponent : null}
      trigger={
        <div>
          <ComponentLibraryBlock
            {...libraryBlockProps}
            cornerUI={
              <InquiriesGatingStickerContainer>
                {gatedSettings.badge}
              </InquiriesGatingStickerContainer>
            }
          />
        </div>
      }
    />
  ) : (
    <ComponentLibraryBlock {...libraryBlockProps} />
  );
};

export default memo(SelfServeComponentBlockWrapper);
